import { App } from '@capacitor/app'
import { useEffect, useRef } from 'react'

import { tenantSlugs } from '@/modules/healthblocks/ServerProvider'
import { useNavigate } from '@/router-wrapper'

import type { TenantSlug } from '@/modules/healthblocks/ServerProvider'

export const DeeplinkListener = () => {
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const inferTenantFromCurrentLocation = (): TenantSlug => {
    const currentFirstSegment = window.location.pathname.split('/')[1]
    const currentTenant = currentFirstSegment as TenantSlug
    return tenantSlugs.includes(currentTenant) ? currentTenant : 'azmm'
  }

  const handleDeeplink = (url: string) => {
    if (!url || !url.includes('/link/')) return

    try {
      const parsed = new URL(url)
      const normalizedPathname =
        parsed.hostname === 'link' && !parsed.pathname.startsWith('/link/')
          ? `/link${parsed.pathname}`
          : parsed.pathname

      const internalPath = `${normalizedPathname}${parsed.search}${parsed.hash}`

      // Prefer explicit tenant from URL host when present (e.g. be.xxx://azmm/link/messages)
      const tenantFromHost = parsed.hostname as TenantSlug
      const tenant = tenantSlugs.includes(tenantFromHost) ? tenantFromHost : inferTenantFromCurrentLocation()

      const finalPath = internalPath.startsWith('/link/') ? `/${tenant}${internalPath}` : internalPath

      // Navigate exactly to what the OS provided (tenant included), without auto-prefixing
      navigateRef.current(finalPath, { replace: true, tenant: false })
    } catch {
      // Fallback: best-effort extraction if URL parsing fails
      const explicitTenantMatch = url.match(/\/(azmm|olvw)\/link\//)
      if (explicitTenantMatch?.index !== undefined) {
        const internalPath = url.slice(explicitTenantMatch.index)
        navigateRef.current(internalPath, { replace: true, tenant: false })
        return
      }

      const index = url.indexOf('/link/')
      if (index === -1) return

      const internalPath = url.slice(index) // "/link/..."
      const finalPath = `/${inferTenantFromCurrentLocation()}${internalPath}`
      navigateRef.current(finalPath, { replace: true, tenant: false })
    }
  }

  useEffect(() => {
    const setupDeeplinks = async () => {
      const launchUrl = await App.getLaunchUrl()

      if (launchUrl?.url) {
        handleDeeplink(launchUrl.url)
      }

      // Fires when the app is already running (background/foreground) and opened via a deep link
      return await App.addListener('appUrlOpen', (data) => {
        handleDeeplink(data?.url ?? '')
      })
    }

    let listener: { remove: () => Promise<void> } | undefined
    setupDeeplinks()
      .then((handle) => {
        listener = handle
      })
      .catch(() => {
        // Ignore: deep link listener is best-effort and should never break app startup
      })

    return () => {
      listener?.remove?.()
    }
  }, [])

  return null
}
